import { pdfLinkProps } from '@/lib/pdf'
import { gatedDownloadProps } from '@/lib/gatedDownload'
import { localizeHref } from '@/i18n/href'
import { useI18n } from '@/i18n/I18nProvider'
import type { Section } from '@/content/sections'

/**
 * The parts every section band may need, and nothing else.
 *
 * Deliberately small. Whatever lives here is read by anybody editing any
 * section, so a helper used by one or two kinds belongs in those kinds' files —
 * the carousels are the worked example, 537 lines that only `features` and
 * `quotes` have ever needed.
 */

/**
 * One member of the `Section` union, by its `kind`.
 *
 * Each renderer takes its own narrowed type, so wiring `Hero` to the `cover`
 * case is a compile error rather than a runtime one. Inside the old switch,
 * TypeScript narrowed `section` for free; across a file boundary it has to be
 * said.
 */
export type SectionOf<K extends Section['kind']> = Extract<Section, { kind: K }>

/**
 * A word or two inside a sentence, made into a link — written in the dictionary
 * as `[Privacy](/en/privacy-policy)`.
 *
 * Asked for on 2026-09-01: *"privacy'e tıklayınca privacy policy'e gidebiliyor
 * olalım"*. The alternative was an item-level `href`, which links the whole
 * card title and cannot point four words in a sentence at four different pages.
 *
 * Only the link markup is markdown; everything else is text and is rendered as
 * text, so a stray bracket in copy is a stray bracket and not a parser bug. The
 * href is an English path like every other href in the dictionary and goes
 * through `localizeHref`; a full URL is passed through and opens in a new tab.
 *
 * The link takes the colour of the sentence it sits in and is marked by an
 * underline, so a body paragraph does not change shape when a word in it
 * becomes clickable.
 */
const INLINE_LINK = /\[([^\]]+)\]\(([^)\s]+)\)/g

export function InlineText({ children }: { children: string }) {
  const { locale } = useI18n()
  const parts: (string | { label: string; href: string })[] = []
  let last = 0
  for (const m of children.matchAll(INLINE_LINK)) {
    if (m.index! > last) parts.push(children.slice(last, m.index))
    parts.push({ label: m[1], href: m[2] })
    last = m.index! + m[0].length
  }
  if (last === 0) return <>{children}</>
  if (last < children.length) parts.push(children.slice(last))

  return (
    <>
      {parts.map((p, i) =>
        typeof p === 'string' ? (
          <span key={i}>{p}</span>
        ) : (
          <a
            key={i}
            href={localizeHref(p.href, locale)}
            {...(p.href.startsWith('http') ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
            className="underline underline-offset-4 transition-colors hover:text-primary"
          >
            {p.label}
          </a>
        ),
      )}
    </>
  )
}

/**
 * One frame for every hero photograph on the site, so there is exactly one place
 * to change it and no page can drift into its own shape.
 *
 * 3:2 with `object-cover`, which is the home page's frame — the picture is
 * *cropped* to fit it, never squashed. Every hero source is between 1.52 and
 * 1.79 wide, so the box only ever trims the left and right edges, and the crop
 * is centred because these are rooms with people in the middle of them.
 *
 * The radius was `rounded-3xl` (24px) and is now 32px on a phone, 40px above
 * `sm` — asked for on 2026-08-26, "daha round olsunlar". 40px is about as far as
 * this can go before the corners start eating the people standing at the edges
 * of the frame; if a further round is asked for, crop the photographs tighter
 * rather than rounding them more.
 */
export const HERO_PHOTO = 'aspect-[3/2] w-full rounded-[2rem] object-cover sm:rounded-[2.5rem]'

/**
 * The same frame, square — the hero box for artwork rather than photography
 * (`photoFrame: 'square'` in content/sections.ts).
 *
 * Same radius, so the two shapes are recognisably one frame in two proportions,
 * and still `object-cover`: the sources this is for are square already, so it
 * crops nothing, and a source that is not square is cropped rather than squashed
 * exactly as it would be in the wide box.
 */
export const SQUARE_PHOTO = 'aspect-square w-full rounded-[2rem] object-cover sm:rounded-[2.5rem]'

/**
 * `tight` trims the pill to `px-6` and a 1rem label. It exists for the hero,
 * whose copy sits in a column under half the row wide above `lg` — at the full
 * pill size two CTAs are wider than that column, so `flex-wrap` stacked them
 * one above the other. Trimming is what keeps them on one line in every locale;
 * Turkish labels are the long ones and they decide the size, not English.
 */
export function Ctas({
  items,
  onDark,
  tight,
  secondaryLight,
}: {
  items?: { label: string; href: string }[]
  onDark?: boolean
  tight?: boolean
  /* Picks `.btn-secondary-light` over `.btn-secondary`. Both are the same
     plain 96%-tint pill as of 2026-09-02 — the outlined `.btn-secondary`
     read as a button stuck in its pressed state — so this currently changes
     nothing and is kept only so the hero can differ again cheaply. */
  secondaryLight?: boolean
}) {
  const { locale } = useI18n()
  if (!items?.length) return null
  const size = tight ? 'px-6 py-3.5 text-base' : 'px-10 py-4 text-[1.125rem]'
  return (
    <div className="mt-10 flex flex-wrap items-center gap-4">
      {items.map((c, i) => (
        <a
          key={c.href + c.label}
          /* A CTA at a PDF is gated as of 2026-08-30: it scrolls to the form at
             the foot of the page and the file is offered once the form is in
             (`lib/gatedDownload.ts`). Everything else keeps the address it was
             given, localised as before. `pdfLinkProps` still applies, and is
             empty for a gated link — `#contact` is not a document, so the new
             tab it used to open is exactly what we no longer want. */
          href={localizeHref(gatedDownloadProps(c.href).href, locale)}
          data-gated-download={gatedDownloadProps(c.href)['data-gated-download']}
          {...pdfLinkProps(gatedDownloadProps(c.href).href)}
          className={
            i === 0
              ? onDark
                ? `inline-flex items-center justify-center rounded-[--radius-pill] bg-background ${size} font-bold text-foreground transition-opacity [transition-duration:var(--transition-base)] hover:opacity-90`
                : `btn-primary${tight ? ` ${size}` : ''}`
              : onDark
                ? `inline-flex items-center justify-center rounded-[--radius-pill] border border-background/30 ${size} font-bold text-background transition-colors [transition-duration:var(--transition-base)] hover:bg-background/10`
                : `${secondaryLight ? 'btn-secondary-light' : 'btn-secondary'}${tight ? ` ${size}` : ''}`
          }
        >
          {c.label}
        </a>
      ))}
    </div>
  )
}
